import { useAnalysisStore } from '../../store/useAnalysisStore';
import { useState, useCallback } from 'react';
import { Drawer, Button } from '@mui/material';
import { useMakeReportSuccessMutation, useRemoveReportMutation } from '../../queries';
import { ANALYSIS_STATUS } from '../../data/define';
import toast from 'react-hot-toast';
import IcCheckOn from '../../assets/image/ic_check_on.svg';
import IcTrash from '../../assets/image/ic_trash.svg';
import IcShare from '../../assets/image/ic_share.svg';
import OneBtnAlert from '../common/OneBtnAlert';
import TwoBtnAlert from '../common/TwoBtnAlert';
import { useTranslation } from '../../hook/useTranslation';

interface MoreDialogProps {
  mainKidId?: number | string | null;
  open: boolean;
  onClose?: (event?: any, reason?: any) => void;
  reloadReports?: () => void;
  selectedReportId?: number | string | null;
  setIsOpenToggleBtn?: (open: boolean) => void;
}

function MoreDialog({
  open,
  onClose = () => {},
  reloadReports = () => {},
  selectedReportId = null,
  setIsOpenToggleBtn = () => {},
}: MoreDialogProps) {
  const { t } = useTranslation();
  const analysisResult = useAnalysisStore((s) => s.analysisResult);
  const [reportCreateOpen, setReportCreateOpen] = useState(false);
  const [reportDeleteOpen, setReportDeleteOpen] = useState(false);
  const [analysisAlertOpen, setAnalysisAlertOpen] = useState(false);

  const { mutate: makeReportSuccess } = useMakeReportSuccessMutation();
  const { mutate: removeReport } = useRemoveReportMutation();

  const handleCreateNewReport = useCallback(() => {
    if (!selectedReportId) return;
    makeReportSuccess(Number(selectedReportId), {
      onSuccess: (res: any) => {
        if (res && res.data?.success) {
          setIsOpenToggleBtn(true);
          onClose();
        } else {
          toast.error(t('report.more.toast_complete_fail'));
        }
      },
      onError: (error) => {
        console.error('Error creating report:', error);
        toast.error(t('report.more.toast_complete_fail'));
      },
      onSettled: () => {
        setReportCreateOpen(false);
      },
    });
  }, [selectedReportId, setIsOpenToggleBtn, onClose, makeReportSuccess, t]);

  const handleReportDelete = useCallback(() => {
    if (!selectedReportId) return;
    removeReport(Number(selectedReportId), {
      onSuccess: (res: any) => {
        if (res && res.data?.success) {
          reloadReports();
          onClose();
        } else {
          toast.error(t('report.more.toast_delete_fail'));
        }
      },
      onError: (error) => {
        console.error('Error deleting report:', error);
        toast.error(t('report.more.toast_delete_fail'));
      },
      onSettled: () => {
        setReportDeleteOpen(false);
      },
    });
  }, [reloadReports, selectedReportId, onClose, removeReport, t]);

  return (
    <Drawer
      open={open}
      anchor="bottom"
      onClose={(event: any, reason: any) => {
        if (reason === 'backdropClick') return;
        if (onClose) (onClose as any)(event, reason);
      }}
      PaperProps={{ sx: { borderRadius: '10px 10px 0 0' } }}
    >
      <div className="relative w-full p-6 min-h-[200px] text-[#1C1C1C] text-[20px] font-[700] font-['Pretendard'] flex flex-col gap-[14px] items-center justify-between bg-white overflow-hidden">
        <Button
          variant="outlined"
          fullWidth
          sx={{ color: '#42A9AA', height: '56px', '&:hover': { color: '#42A9AA' } }}
          onClick={() => {
            if (analysisResult.status === ANALYSIS_STATUS.PENDING) {
              setAnalysisAlertOpen(true);
            } else {
              setReportCreateOpen(true);
            }
          }}
        >
          <img src={IcCheckOn} alt="Share" className="w-[24px] h-[24px] mr-2" />
          <span className="text-[18px]">{t('report.more.create_new')}</span>
        </Button>
        <div className="text-[14px] text-[#767676] text-left w-full mt-[-9px] font-[400]">
          {t('report.more.create_new_guide')}
        </div>
        <Button
          variant="outlined"
          fullWidth
          sx={{ color: '#FF3E3E', height: '56px', '&:hover': { color: '#FF3E3E' } }}
          onClick={() => {
            if (analysisResult.status === ANALYSIS_STATUS.PENDING) {
              setAnalysisAlertOpen(true);
            } else {
              setReportDeleteOpen(true);
            }
          }}
        >
          <img src={IcTrash} alt="Share" className="w-[24px] h-[24px] mr-2" />
          <span className="text-[18px]">{t('report.more.delete')}</span>
        </Button>
        <Button variant="grayPainted" fullWidth sx={{ color: '#969696', height: '56px' }}>
          <img src={IcShare} alt="Share" className="w-[24px] h-[24px] mr-2" />
          <span className="text-[18px]">{t('report.more.share')}</span>
        </Button>
        <Button
          variant="outlined"
          onClick={onClose}
          sx={{ color: '#767676', mt: '12px', fontSize: '18px', '&:hover': { color: '#767676' } }}
          fullWidth
        >
          {t('common.close')}
        </Button>
      </div>
      <OneBtnAlert
        open={analysisAlertOpen}
        onClose={() => setAnalysisAlertOpen(false)}
        title={t('nav.alert.analysis_in_progress.title')}
        description={t('nav.alert.analysis_in_progress.description')}
        btnText={t('common.confirm')}
        onClick={() => setAnalysisAlertOpen(false)}
      />
      <TwoBtnAlert
        open={reportCreateOpen}
        onClose={() => setReportCreateOpen(false)}
        title={t('report.more.alert_complete_title')}
        content={t('report.more.alert_complete_content')}
        leftBtnText={t('common.cancel')}
        rightBtnText={t('report.more.create_new')}
        leftBtnClick={() => setReportCreateOpen(false)}
        rightBtnClick={handleCreateNewReport}
      />
      <TwoBtnAlert
        open={reportDeleteOpen}
        onClose={() => setReportDeleteOpen(false)}
        title={t('report.more.alert_delete_title')}
        content={t('report.more.alert_delete_content')}
        leftBtnText={t('common.cancel')}
        rightBtnText={t('report.more.delete')}
        leftBtnClick={() => setReportDeleteOpen(false)}
        rightBtnClick={handleReportDelete}
      />
    </Drawer>
  );
}

export default MoreDialog;
